// signup-form.js — pure helpers for the login page's signup wizard (C1496)
// and the profile page (TPT81). DOM-free, node-testable, mirrors the
// lib/vcs-form.js pattern: this module BUILDS/VALIDATES client-side shapes,
// the server-side twin (api/src/lib/avatar.js, api/src/lib/signup-codes.js,
// routes/auth.js) is the actual security boundary — nothing here is trusted
// server-side.

// experience_years is a TINYINT 0-99 (api/src/migrations/063_user_profile_fields.js).
// Buckets store the lower bound of the range so the stored value stays an
// honest integer instead of a made-up sentinel. '' means "field left blank"
// and must be omitted from the request body entirely (see buildSignupBody).
export const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 0, label: 'Less than a year' },
  { value: 1, label: '1 year' },
  { value: 2, label: '2 years' },
  { value: 3, label: '3 years' },
  { value: 4, label: '4 years' },
  { value: 5, label: '5 years' },
  { value: 6, label: '6–9 years' },
  { value: 10, label: '10–14 years' },
  { value: 15, label: '15–19 years' },
  { value: 20, label: '20+ years' },
];

// Client twins of api/src/lib/avatar.js's AVATAR_MIME_TYPES/MAX_AVATAR_BYTES —
// used to steer the file picker (`accept`) and the client-side downscale loop
// (login.js) toward what the server will actually accept. The server's own
// regex/magic-byte check is what actually enforces this; these constants only
// avoid a doomed round-trip.
export const AVATAR_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_AVATAR_BYTES = 512 * 1024;

// Decoded byte length of a `data:<mime>;base64,<payload>` string, without
// allocating a Buffer/Uint8Array — same 3/4-minus-padding formula
// api/src/lib/avatar.js gets for free from `Buffer.from(data,'base64').length`.
// Returns 0 for anything that isn't a data URL with a comma.
export function dataUrlByteLength(dataUrl) {
  if (typeof dataUrl !== 'string') return 0;
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx === -1) return 0;
  const b64 = dataUrl.slice(commaIdx + 1);
  if (!b64) return 0;
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

// Builds the POST /auth/signup/complete body. `fields`: { name, password,
// avatar, headline, background, experience_years }. Optional fields are
// OMITTED (not sent as null/'') when blank — the server treats any *present*
// key as a value to validate, so an empty string for headline would fail its
// own length/type check instead of being treated as "not provided".
//
// experience_years === 0 ("Less than a year") is a real, falsy value — the
// omit test below is `!== '' && != null`, deliberately not a truthiness
// check, or bucket 0 would silently vanish from every submission.
export function buildSignupBody(signupToken, fields = {}) {
  const body = { signup_token: signupToken, password: fields.password };

  if (typeof fields.name === 'string' && fields.name.trim()) {
    body.name = fields.name.trim();
  }
  if (typeof fields.avatar === 'string' && fields.avatar) {
    body.avatar = fields.avatar;
  }
  if (typeof fields.headline === 'string' && fields.headline.trim()) {
    body.headline = fields.headline.trim();
  }
  if (typeof fields.background === 'string' && fields.background.trim()) {
    body.background = fields.background.trim();
  }
  if (fields.experience_years !== '' && fields.experience_years !== undefined && fields.experience_years !== null) {
    body.experience_years = Number(fields.experience_years);
  }

  return body;
}

// Builds the PATCH /auth/me body (TPT81, pages/profile.js). Semantics are
// the INVERSE of buildSignupBody above — read both before touching either:
//   signup  : a blank optional is OMITTED (a present key would be validated)
//   profile : a blank field is SENT as '' (that is how the server CLEARS it);
//             an omitted key means "leave unchanged" (api/src/routes/auth.js
//             PATCH /me). The profile page always sends the whole form (it
//             IS the desired end state), so name/headline/background/
//             experience_years are always present — that also makes the
//             server's `{}` -> 400 "No profile fields to update" branch
//             unreachable from this client.
//
// `fields.avatar` is tri-state and the ONLY omittable key:
//   undefined -> unchanged (key absent from the body)
//   null      -> clear the uploaded avatar
//   string    -> the new data: URL
//
// experience_years: the '' test MUST come before Number(), since Number('')
// is 0 — the same trap the server-side handler guards against on its side.
export function buildProfilePatchBody(fields = {}) {
  const str = (v) => (typeof v === 'string' ? v.trim() : '');
  const exp = fields.experience_years;

  const body = {
    name: str(fields.name),
    headline: str(fields.headline),
    background: str(fields.background),
    experience_years: (exp === '' || exp === undefined || exp === null) ? '' : Number(exp),
  };

  if (fields.avatar === null) {
    body.avatar = null;
  } else if (typeof fields.avatar === 'string' && fields.avatar) {
    body.avatar = fields.avatar;
  }

  return body;
}

// Inline error string for a failed PATCH /auth/me save. Callers must handle
// a 401 themselves (the session died mid-edit — toast + navigate to login,
// not an inline message) BEFORE calling this, same contract as
// signupErrorText's 'complete' step.
export function profileErrorText(err) {
  const status = err && err.status;
  const message = (err && err.message) || '';
  if (status === 413) return 'That photo is too large — pick a smaller image.';
  return message || 'Failed to save profile';
}

// Maps an apiFetch error ({status, message, body}) to the inline string to
// show for a given wizard step ('start' | 'verify' | 'complete'). Callers
// must special-case a `complete` 401 themselves (restart the wizard — see
// login.js) BEFORE calling this; that path is a pane transition + toast, not
// an inline message, so it isn't handled here.
export function signupErrorText(err, step) {
  const status = err && err.status;
  const message = (err && err.message) || '';

  // TPT103 — 409 { reason: 'code_quota_exhausted' } comes back from
  // 'complete' only (the reservation is checked inside the register
  // transaction, see routes/auth.js). Checked ahead of the generic
  // per-step 409 branches below, which would otherwise show "Already
  // registered — sign in instead" and send a teammate to the login form.
  if (err.body && err.body.reason === 'code_quota_exhausted') {
    return 'This registration code has reached its participant limit.';
  }

  // TPT96 — 403 { beta_required: true } can come back from either 'start' or
  // 'complete' (server re-checks the beta gate at both, since /complete's
  // request is the one that actually creates the account). Checked ahead of
  // every step-specific branch below.
  if (status === 403 && err.body && err.body.beta_required) {
    return 'Beta access required — request an invite at tipatask.com.';
  }

  if (step === 'start') {
    if (status === 409) {
      return err.body && err.body.google_only
        ? 'Already registered with Google — use Sign in with Google.'
        : 'Already registered — sign in instead.';
    }
    if (status === 502) {
      return 'Couldn’t send the verification email — try again in a minute.';
    }
    return message || 'Failed to send code';
  }

  if (step === 'verify') {
    return message || 'Invalid code';
  }

  // step === 'complete' (401 handled separately by the caller)
  if (status === 409) {
    return message || 'Already registered — sign in instead.';
  }
  return message || 'Failed to create account';
}

// TPT96 — client-side shape check for a typed/pasted registration code.
// Mirrors api/src/lib/beta-codes.js's normalizeBetaCode() exactly (same
// alphabet, same 10-char length, same lowercase/strip-non-alphabet rule) so
// the "enter your code" form can catch an obviously-wrong paste before a
// round-trip — the server is still the real boundary, this is UX only.
const BETA_CODE_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
export function normalizeBetaCodeInput(raw) {
  if (typeof raw !== 'string') return null;
  const stripped = raw.toLowerCase().replace(new RegExp(`[^${BETA_CODE_ALPHABET}]`, 'g'), '');
  return stripped.length === 10 ? stripped : null;
}

// Inline error string for POST /auth/beta/validate (pages/register.js).
export function betaCodeErrorText(err) {
  const status = err && err.status;
  const message = (err && err.message) || '';
  if (status === 400) return 'Enter your registration code';
  if (status === 404) return 'That code is not valid — check it and try again.';
  if (status === 409 || (err && err.body && err.body.used)) return 'That code has already been used — try signing in.';
  if (status === 410 || (err && err.body && err.body.expired)) return 'That code has expired — ask us to resend it.';
  if (status === 429) return 'Too many attempts. Try again in a few minutes.';
  return message || 'Could not check that code';
}

// The three attribute values a password eye-toggle button carries in each
// state (pages/login.js's togglePasswordVisibility()). `pressed` is already a
// string so it can go straight into setAttribute('aria-pressed', …) — an
// aria-* attribute holding "false" is a real state, not an absent one.
export function passwordToggleAttrs(revealed) {
  return revealed
    ? { type: 'text', label: 'Hide password', pressed: 'true' }
    : { type: 'password', label: 'Show password', pressed: 'false' };
}
